import { MutableRefObject, useState } from 'react';
import { useAppDispatch } from 'app/store';
import { useIsMounted } from 'app/utils/hooks';
import { _SongsLinks } from 'types/song';
import { fetchSongsWithAudioListsEffect } from 'app/store/SongList/effects';
import {
  AudioSelectionItem,
  FetchAudioOptions,
  getAudioListFromSongs,
} from 'app/components/Audio';

interface UseFetchAudioProps {
  searchRef: MutableRefObject<string>;
  filterTypesRef: MutableRefObject<{
    types: string[];
    onlyLatestVersionAudio: boolean;
  }>;
  audioList: AudioSelectionItem[];
  setFetchingMore: (state: boolean) => void;
  setAudioList: (state: AudioSelectionItem[]) => void;
  checkAllOptions?: string[];
}

const formatRequestFilters = ({
  types,
  checkAllOptions,
}: {
  types?: string[];
  checkAllOptions?: string[];
}) => {
  if (checkAllOptions && !types?.length) {
    return checkAllOptions || [];
  }
  return types;
};

export const useFetchAudio = ({
  searchRef,
  filterTypesRef,
  audioList,
  setFetchingMore,
  setAudioList,
  checkAllOptions,
}: UseFetchAudioProps) => {
  const dispatch = useAppDispatch();
  const isMounted = useIsMounted();
  const [links, setLinks] = useState<_SongsLinks>();

  const fetchAudio = async (options?: FetchAudioOptions) => {
    const {
      next,
      isRootFunctionCall = true,
      search,
      validationContext,
      filters,
    } = options || {};
    const shouldCancel =
      search !== searchRef.current ||
      filters?.types !== filterTypesRef.current.types ||
      !isMounted();
    if (shouldCancel) {
      return;
    }
    try {
      if (isRootFunctionCall) {
        setFetchingMore(true);
      }
      const { items, _links } = await dispatch(
        fetchSongsWithAudioListsEffect({
          next: next || links?.next,
          search,
          validationContext,
          types: formatRequestFilters({
            types: filters?.types,
            checkAllOptions,
          }),
          onlyLatestVersionAudio: filters?.onlyLatestVersionAudio,
        })
      ).unwrap();

      const newAudioItems = getAudioListFromSongs(items);
      if (newAudioItems.length === 0 && _links.next) {
        await fetchAudio({
          next: _links.next,
          isRootFunctionCall: false,
          search,
          filters,
          validationContext,
        });
      } else if (isMounted()) {
        setLinks(_links);
        setAudioList([...audioList, ...newAudioItems]);
      }

      if (isRootFunctionCall && isMounted()) {
        setFetchingMore(false);
      }
    } catch {
      // proceed
    }
  };

  return { fetchAudio, links, setLinks };
};
